import type { Cents } from '../types/index.js';

/** Format cents as USD currency string (e.g., 12500 → "$125.00"). */
export function formatCents(cents: Cents, locale = 'en-US', currency = 'USD'): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
  }).format(cents / 100);
}

/** Convert dollar amount to cents, rounding to avoid float imprecision. */
export function dollarsToCents(dollars: number): Cents {
  return Math.round(dollars * 100);
}

/** Current UTC timestamp as ISO string. */
export function nowUtc(): string {
  return new Date().toISOString();
}

/**
 * Build a Cloudflare Image Transformations URL for photos served from
 * `images.auctisync.com`. Transformations are applied on-demand at the
 * edge, cached per-unique URL, and automatically served as AVIF/WebP/JPEG
 * based on the client's Accept header (via `format=auto`).
 *
 * Pass a `storage_path` (the R2 object key) OR a full `display_url`; both
 * work. Non-R2 URLs (legacy Module #3 Supabase Storage photos) are passed
 * through untouched — the transform gateway only wraps our own images.
 *
 * See: https://developers.cloudflare.com/images/transform-images/
 */
const IMAGES_ORIGIN = 'https://images.auctisync.com';

export interface ImageVariantOptions {
  /** Target width in px. The height scales proportionally. */
  width?: number;
  /** Target height in px; usually omit and let the aspect ratio do the work. */
  height?: number;
  /** JPEG/WebP quality 1-100. Default 80 (indistinguishable from 100 at typical sizes). */
  quality?: number;
  /** 'auto' (negotiate by Accept header), 'webp', 'avif', 'jpeg', or 'png'. Default 'auto'. */
  format?: 'auto' | 'webp' | 'avif' | 'jpeg' | 'png';
  /** Resize mode: 'scale-down' (never upscale), 'contain', 'cover', 'crop', 'pad'. Default 'scale-down'. */
  fit?: 'scale-down' | 'contain' | 'cover' | 'crop' | 'pad';
}

export function imageUrl(
  source: string | null | undefined,
  opts: ImageVariantOptions = {},
): string {
  if (!source) return '';
  // Only transform images on our R2 custom domain. Legacy Supabase Storage
  // URLs (from Module #3) don't go through our Cloudflare zone so the
  // transform wrapper would return a 404.
  if (!source.startsWith(IMAGES_ORIGIN + '/')) return source;

  const key = source.slice(IMAGES_ORIGIN.length + 1);
  const parts: string[] = [];
  if (opts.width) parts.push(`width=${opts.width}`);
  if (opts.height) parts.push(`height=${opts.height}`);
  parts.push(`quality=${opts.quality ?? 80}`);
  parts.push(`format=${opts.format ?? 'auto'}`);
  parts.push(`fit=${opts.fit ?? 'scale-down'}`);
  return `${IMAGES_ORIGIN}/cdn-cgi/image/${parts.join(',')}/${key}`;
}

/** Preset for a small card thumbnail. Used in lot grids + dashboard tiles. */
export function thumbUrl(source: string | null | undefined): string {
  return imageUrl(source, { width: 400 });
}

/** Preset for a full-size detail view (desktop + mobile). */
export function detailUrl(source: string | null | undefined): string {
  return imageUrl(source, { width: 1400 });
}

/** Preset for AI vision input — balanced for token cost vs. detail retention. */
export function aiVisionUrl(source: string | null | undefined): string {
  return imageUrl(source, { width: 1600, quality: 82 });
}
